// SPDX-License-Identifier: AGPL-3.0-only

import { requestJson } from "./http.js";

export type ProbeHealthStatus = "healthy" | "overdue" | "never_reported" | "disabled" | "unknown";

export interface ProbeStorageDataLossSummary {
  readonly lastReportedAt: string;
  readonly dataLossEvents: number;
  readonly lostMeasurements: number;
  readonly lostRecords: number;
  readonly lostBytes: number;
  readonly reason: string;
}

export interface ProbeUploadDiagnosticsSummary {
  readonly lastReportedAt: string;
  readonly agentVersion: string | null;
  readonly uptimeMs: number;
  readonly resetReason: number | null;
  readonly resetReasonLabel: string | null;
  readonly bootCount: number | null;
  readonly chipTemperatureCelsius: number | null;
  readonly heap?: {
    readonly freeBytes: number | null;
    readonly minimumFreeBytes: number | null;
    readonly largestFreeBlockBytes: number | null;
  };
  readonly upload?: {
    readonly lastSuccessfulUploadAt: string;
    readonly failuresLast24h: number | null;
    readonly consecutiveFailures: number | null;
  };
  readonly storage?: {
    readonly sizeBytes: number | null;
    readonly usedBytes: number | null;
    readonly headOffset: number | null;
    readonly appendOffset: number | null;
    readonly validRecords: number | null;
    readonly measurementRecords: number | null;
    readonly wrapped: boolean | null;
  };
  readonly wifi?: {
    readonly stationConnected: boolean | null;
    readonly rssi: number | null;
    readonly channel: number | null;
    readonly authMode: number | null;
  };
}

export interface ProbeFirmwareInventory {
  readonly firmwareVersion: string | null;
  readonly otaCapable: boolean;
  readonly updateStatus: "current" | "update_available" | "update_disabled" | "mandatory_update";
  readonly tenantDefaultChannel: "stable" | "beta";
  readonly tenantDefaultUpdateMode: "manual" | "automatic" | "disabled";
  readonly channelOverride: "inherit" | "stable" | "beta";
  readonly updateModeOverride: "inherit" | "manual" | "automatic" | "disabled";
  readonly effectiveChannel: "stable" | "beta";
  readonly effectiveUpdateMode: "manual" | "automatic" | "disabled";
  readonly updateAvailable: boolean;
  readonly targetVersion: string | null;
  readonly targetReleaseId: string | null;
  readonly targetMandatory: boolean;
  readonly manualUpdatePending: boolean;
  readonly lastUpdatePhase: string | null;
  readonly lastUpdateResult: string | null;
  readonly lastUpdateErrorCode: string | null;
  readonly lastUpdateMessage: string | null;
  readonly lastUpdateAt: string | null;
}

export interface ProbeInventoryItem {
  readonly id: string;
  readonly displayName: string;
  readonly enabled: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly lastReceivedAt: string | null;
  readonly lastMeasuredAt: string | null;
  readonly nextExpectedAt: string | null;
  readonly overdueMs: number;
  readonly healthStatus: ProbeHealthStatus;
  readonly agentVersion: string | null;
  readonly probeModel: string | null;
  readonly currentBootId: string | null;
  readonly desiredConfigurationVersion: number | null;
  readonly reportedAppliedConfigurationVersion: number | null;
  readonly effectiveConfigurationVersion: number | null;
  readonly expectedUploadIntervalMs: number | null;
  readonly primaryTargetUrl: string | null;
  readonly primaryTargetExpectedStatus: number | null;
  readonly secondaryTargetCount: number;
  readonly lastConfigurationAttemptVersion: number | null;
  readonly lastConfigurationAttemptStatus: string | null;
  readonly lastConfigurationErrorCode: string | null;
  readonly enrollmentStatus: "bound" | "activated" | null;
  readonly activationConfirmedAt: string | null;
  readonly storageDataLoss: ProbeStorageDataLossSummary | null;
  readonly uploadDiagnostics: ProbeUploadDiagnosticsSummary | null;
  readonly firmware: ProbeFirmwareInventory;
}
export interface ExpectedProviderReconnectPolicy {
  readonly enabled: boolean; readonly timezone: string; readonly windowStartLocal: string; readonly windowEndLocal: string; readonly maxDurationMs: number; readonly createdAt: string; readonly updatedAt: string;
}
export type UpdateExpectedProviderReconnectPolicy = Omit<ExpectedProviderReconnectPolicy, "createdAt" | "updatedAt">;

interface ProbeListResponse {
  readonly probes?: unknown;
}

const HEALTH_STATUSES: ReadonlySet<string> = new Set<string>([
  "healthy",
  "overdue",
  "never_reported",
  "disabled",
  "unknown",
]);
const ENROLLMENT_STATUSES: ReadonlySet<string> = new Set<string>(["bound", "activated"]);
const CHANNELS: ReadonlySet<string> = new Set<string>(["stable", "beta"]);
const UPDATE_MODES: ReadonlySet<string> = new Set<string>(["manual", "automatic", "disabled"]);
const UPDATE_STATUSES: ReadonlySet<string> = new Set<string>(["current", "update_available", "update_disabled", "mandatory_update"]);
const POLICY_OVERRIDES: ReadonlySet<string> = new Set<string>(["inherit", "manual", "automatic", "disabled", "stable", "beta"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown, fieldName: string): string {
  if (typeof value !== "string") {
    throw new Error(`Probe response field ${fieldName} is invalid`);
  }
  return value;
}

function readNullableString(value: unknown, fieldName: string): string | null {
  if (value === null) {
    return null;
  }
  return readString(value, fieldName);
}

function readNullableNumber(value: unknown, fieldName: string): number | null {
  if (value === null) {
    return null;
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Probe response field ${fieldName} is invalid`);
  }
  return value;
}

function readProbe(payload: unknown, fieldName: string): ProbeInventoryItem {
  if (!isRecord(payload)) {
    throw new Error(`Probe response field ${fieldName} is invalid`);
  }
  if (typeof payload.enabled !== "boolean") {
    throw new Error(`Probe response field ${fieldName}.enabled is invalid`);
  }
  if (typeof payload.overdueMs !== "number" || !Number.isFinite(payload.overdueMs)) {
    throw new Error(`Probe response field ${fieldName}.overdueMs is invalid`);
  }
  if (typeof payload.secondaryTargetCount !== "number" || !Number.isFinite(payload.secondaryTargetCount)) {
    throw new Error(`Probe response field ${fieldName}.secondaryTargetCount is invalid`);
  }
  const healthStatus: string = readString(payload.healthStatus, `${fieldName}.healthStatus`);
  if (!HEALTH_STATUSES.has(healthStatus)) {
    throw new Error(`Probe response field ${fieldName}.healthStatus is invalid`);
  }
  if (
    payload.enrollmentStatus !== null &&
    (typeof payload.enrollmentStatus !== "string" || !ENROLLMENT_STATUSES.has(payload.enrollmentStatus))
  ) {
    throw new Error(`Probe response field ${fieldName}.enrollmentStatus is invalid`);
  }
  const storageDataLoss: ProbeStorageDataLossSummary | null = readStorageDataLoss(
    payload.storageDataLoss,
    `${fieldName}.storageDataLoss`,
  );
  const uploadDiagnostics: ProbeUploadDiagnosticsSummary | null = readUploadDiagnostics(
    payload.uploadDiagnostics,
    `${fieldName}.uploadDiagnostics`,
  );
  const firmware: ProbeFirmwareInventory = readFirmwareInventory(payload.firmware, `${fieldName}.firmware`);

  return {
    id: readString(payload.id, `${fieldName}.id`),
    displayName: readString(payload.displayName, `${fieldName}.displayName`),
    enabled: payload.enabled,
    createdAt: readString(payload.createdAt, `${fieldName}.createdAt`),
    updatedAt: readString(payload.updatedAt, `${fieldName}.updatedAt`),
    lastReceivedAt: readNullableString(payload.lastReceivedAt, `${fieldName}.lastReceivedAt`),
    lastMeasuredAt: readNullableString(payload.lastMeasuredAt, `${fieldName}.lastMeasuredAt`),
    nextExpectedAt: readNullableString(payload.nextExpectedAt, `${fieldName}.nextExpectedAt`),
    overdueMs: payload.overdueMs,
    healthStatus: healthStatus as ProbeHealthStatus,
    agentVersion: readNullableString(payload.agentVersion, `${fieldName}.agentVersion`),
    probeModel: readNullableString(payload.probeModel, `${fieldName}.probeModel`),
    currentBootId: readNullableString(payload.currentBootId, `${fieldName}.currentBootId`),
    desiredConfigurationVersion: readNullableNumber(
      payload.desiredConfigurationVersion,
      `${fieldName}.desiredConfigurationVersion`,
    ),
    reportedAppliedConfigurationVersion: readNullableNumber(
      payload.reportedAppliedConfigurationVersion,
      `${fieldName}.reportedAppliedConfigurationVersion`,
    ),
    effectiveConfigurationVersion: readNullableNumber(
      payload.effectiveConfigurationVersion,
      `${fieldName}.effectiveConfigurationVersion`,
    ),
    expectedUploadIntervalMs: readNullableNumber(
      payload.expectedUploadIntervalMs,
      `${fieldName}.expectedUploadIntervalMs`,
    ),
    primaryTargetUrl: readNullableString(payload.primaryTargetUrl, `${fieldName}.primaryTargetUrl`),
    primaryTargetExpectedStatus: readNullableNumber(
      payload.primaryTargetExpectedStatus,
      `${fieldName}.primaryTargetExpectedStatus`,
    ),
    secondaryTargetCount: payload.secondaryTargetCount,
    lastConfigurationAttemptVersion: readNullableNumber(
      payload.lastConfigurationAttemptVersion,
      `${fieldName}.lastConfigurationAttemptVersion`,
    ),
    lastConfigurationAttemptStatus: readNullableString(
      payload.lastConfigurationAttemptStatus,
      `${fieldName}.lastConfigurationAttemptStatus`,
    ),
    lastConfigurationErrorCode: readNullableString(
      payload.lastConfigurationErrorCode,
      `${fieldName}.lastConfigurationErrorCode`,
    ),
    enrollmentStatus: payload.enrollmentStatus as "bound" | "activated" | null,
    activationConfirmedAt: readNullableString(
      payload.activationConfirmedAt,
      `${fieldName}.activationConfirmedAt`,
    ),
    storageDataLoss,
    uploadDiagnostics,
    firmware,
  };
}

function readRequiredNumber(value: unknown, fieldName: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Probe response field ${fieldName} is invalid`);
  }
  return value;
}

function readStorageDataLoss(value: unknown, fieldName: string): ProbeStorageDataLossSummary | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (!isRecord(value)) {
    throw new Error(`Probe response field ${fieldName} is invalid`);
  }
  return {
    lastReportedAt: readString(value.lastReportedAt, `${fieldName}.lastReportedAt`),
    dataLossEvents: readRequiredNumber(value.dataLossEvents, `${fieldName}.dataLossEvents`),
    lostMeasurements: readRequiredNumber(value.lostMeasurements, `${fieldName}.lostMeasurements`),
    lostRecords: readRequiredNumber(value.lostRecords, `${fieldName}.lostRecords`),
    lostBytes: readRequiredNumber(value.lostBytes, `${fieldName}.lostBytes`),
    reason: readString(value.reason, `${fieldName}.reason`),
  };
}

function readOptionalDiagnosticsSection(value: unknown, fieldName: string): Record<string, unknown> | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (!isRecord(value)) {
    throw new Error(`Probe response field ${fieldName} is invalid`);
  }
  return value;
}

function readNullableBoolean(value: unknown, fieldName: string): boolean | null {
  if (value === null) {
    return null;
  }
  if (typeof value !== "boolean") {
    throw new Error(`Probe response field ${fieldName} is invalid`);
  }
  return value;
}

function readUploadDiagnostics(value: unknown, fieldName: string): ProbeUploadDiagnosticsSummary | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (!isRecord(value)) {
    throw new Error(`Probe response field ${fieldName} is invalid`);
  }
  const heap: Record<string, unknown> | undefined = readOptionalDiagnosticsSection(value.heap, `${fieldName}.heap`);
  const upload: Record<string, unknown> | undefined = readOptionalDiagnosticsSection(value.upload, `${fieldName}.upload`);
  const storage: Record<string, unknown> | undefined = readOptionalDiagnosticsSection(value.storage, `${fieldName}.storage`);
  const wifi: Record<string, unknown> | undefined = readOptionalDiagnosticsSection(value.wifi, `${fieldName}.wifi`);

  return {
    lastReportedAt: readString(value.lastReportedAt, `${fieldName}.lastReportedAt`),
    agentVersion: readNullableString(value.agentVersion, `${fieldName}.agentVersion`),
    uptimeMs: readRequiredNumber(value.uptimeMs, `${fieldName}.uptimeMs`),
    resetReason: readNullableNumber(value.resetReason, `${fieldName}.resetReason`),
    resetReasonLabel: readNullableString(value.resetReasonLabel, `${fieldName}.resetReasonLabel`),
    bootCount: readNullableNumber(value.bootCount, `${fieldName}.bootCount`),
    chipTemperatureCelsius: readNullableNumber(
      value.chipTemperatureCelsius,
      `${fieldName}.chipTemperatureCelsius`,
    ),
    ...(heap === undefined
      ? {}
      : {
        heap: {
          freeBytes: readNullableNumber(heap.freeBytes, `${fieldName}.heap.freeBytes`),
          minimumFreeBytes: readNullableNumber(heap.minimumFreeBytes, `${fieldName}.heap.minimumFreeBytes`),
          largestFreeBlockBytes: readNullableNumber(
            heap.largestFreeBlockBytes,
            `${fieldName}.heap.largestFreeBlockBytes`,
          ),
        },
      }),
    ...(upload === undefined
      ? {}
      : {
        upload: {
          lastSuccessfulUploadAt: readString(upload.lastSuccessfulUploadAt, `${fieldName}.upload.lastSuccessfulUploadAt`),
          failuresLast24h: readNullableNumber(upload.failuresLast24h, `${fieldName}.upload.failuresLast24h`),
          consecutiveFailures: readNullableNumber(
            upload.consecutiveFailures,
            `${fieldName}.upload.consecutiveFailures`,
          ),
        },
      }),
    ...(storage === undefined
      ? {}
      : {
        storage: {
          sizeBytes: readNullableNumber(storage.sizeBytes, `${fieldName}.storage.sizeBytes`),
          usedBytes: readNullableNumber(storage.usedBytes, `${fieldName}.storage.usedBytes`),
          headOffset: readNullableNumber(storage.headOffset, `${fieldName}.storage.headOffset`),
          appendOffset: readNullableNumber(storage.appendOffset, `${fieldName}.storage.appendOffset`),
          validRecords: readNullableNumber(storage.validRecords, `${fieldName}.storage.validRecords`),
          measurementRecords: readNullableNumber(
            storage.measurementRecords,
            `${fieldName}.storage.measurementRecords`,
          ),
          wrapped: readNullableBoolean(storage.wrapped, `${fieldName}.storage.wrapped`),
        },
      }),
    ...(wifi === undefined
      ? {}
      : {
        wifi: {
          stationConnected: readNullableBoolean(wifi.stationConnected, `${fieldName}.wifi.stationConnected`),
          rssi: readNullableNumber(wifi.rssi, `${fieldName}.wifi.rssi`),
          channel: readNullableNumber(wifi.channel, `${fieldName}.wifi.channel`),
          authMode: readNullableNumber(wifi.authMode, `${fieldName}.wifi.authMode`),
        },
      }),
  };
}

function readFirmwareInventory(value: unknown, fieldName: string): ProbeFirmwareInventory {
  if (!isRecord(value)) {
    throw new Error(`Probe response field ${fieldName} is invalid`);
  }
  if (typeof value.otaCapable !== "boolean" || typeof value.updateAvailable !== "boolean" || typeof value.manualUpdatePending !== "boolean") {
    throw new Error(`Probe response field ${fieldName} is invalid`);
  }
  if (typeof value.targetMandatory !== "boolean") {
    throw new Error(`Probe response field ${fieldName}.targetMandatory is invalid`);
  }
  const tenantDefaultChannel: string = readString(value.tenantDefaultChannel, `${fieldName}.tenantDefaultChannel`);
  const effectiveChannel: string = readString(value.effectiveChannel, `${fieldName}.effectiveChannel`);
  const channelOverride: string = readString(value.channelOverride, `${fieldName}.channelOverride`);
  const tenantDefaultUpdateMode: string = readString(value.tenantDefaultUpdateMode, `${fieldName}.tenantDefaultUpdateMode`);
  const effectiveUpdateMode: string = readString(value.effectiveUpdateMode, `${fieldName}.effectiveUpdateMode`);
  const updateModeOverride: string = readString(value.updateModeOverride, `${fieldName}.updateModeOverride`);
  const updateStatus: string = readString(value.updateStatus, `${fieldName}.updateStatus`);
  if (
    !CHANNELS.has(tenantDefaultChannel) ||
    !CHANNELS.has(effectiveChannel) ||
    !POLICY_OVERRIDES.has(channelOverride) ||
    !UPDATE_MODES.has(tenantDefaultUpdateMode) ||
    !UPDATE_MODES.has(effectiveUpdateMode) ||
    !POLICY_OVERRIDES.has(updateModeOverride) ||
    !UPDATE_STATUSES.has(updateStatus)
  ) {
    throw new Error(`Probe response field ${fieldName} contains invalid policy values`);
  }
  return {
    firmwareVersion: readNullableString(value.firmwareVersion, `${fieldName}.firmwareVersion`),
    otaCapable: value.otaCapable,
    updateStatus: updateStatus as "current" | "update_available" | "update_disabled" | "mandatory_update",
    tenantDefaultChannel: tenantDefaultChannel as "stable" | "beta",
    tenantDefaultUpdateMode: tenantDefaultUpdateMode as "manual" | "automatic" | "disabled",
    channelOverride: channelOverride as "inherit" | "stable" | "beta",
    updateModeOverride: updateModeOverride as "inherit" | "manual" | "automatic" | "disabled",
    effectiveChannel: effectiveChannel as "stable" | "beta",
    effectiveUpdateMode: effectiveUpdateMode as "manual" | "automatic" | "disabled",
    updateAvailable: value.updateAvailable,
    targetVersion: readNullableString(value.targetVersion, `${fieldName}.targetVersion`),
    targetReleaseId: readNullableString(value.targetReleaseId, `${fieldName}.targetReleaseId`),
    targetMandatory: value.targetMandatory,
    manualUpdatePending: value.manualUpdatePending,
    lastUpdatePhase: readNullableString(value.lastUpdatePhase, `${fieldName}.lastUpdatePhase`),
    lastUpdateResult: readNullableString(value.lastUpdateResult, `${fieldName}.lastUpdateResult`),
    lastUpdateErrorCode: readNullableString(value.lastUpdateErrorCode, `${fieldName}.lastUpdateErrorCode`),
    lastUpdateMessage: readNullableString(value.lastUpdateMessage, `${fieldName}.lastUpdateMessage`),
    lastUpdateAt: readNullableString(value.lastUpdateAt, `${fieldName}.lastUpdateAt`),
  };
}

export async function listTenantProbes(tenantId: string): Promise<readonly ProbeInventoryItem[]> {
  const payload: ProbeListResponse = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/probes`,
  ) as ProbeListResponse;

  if (!Array.isArray(payload.probes)) {
    throw new Error("Probe response field probes is invalid");
  }

  return payload.probes.map((probe: unknown, index: number): ProbeInventoryItem =>
    readProbe(probe, `probes[${index}]`),
  );
}

export async function renameProbe(
  tenantId: string,
  probeId: string,
  displayName: string,
): Promise<ProbeInventoryItem> {
  const payload: unknown = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/probes/${encodeURIComponent(probeId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ displayName }),
    },
  );

  return readProbe(payload, "probe");
}

export async function deleteProbe(tenantId: string, probeId: string): Promise<void> {
  await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/probes/${encodeURIComponent(probeId)}`,
    { method: "DELETE" },
  );
}

function readExpectedProviderReconnectPolicy(value: unknown): ExpectedProviderReconnectPolicy {
  if (!isRecord(value) || typeof value.enabled !== "boolean") throw new Error("Expected provider reconnect policy response is invalid");
  return { enabled: value.enabled, timezone: readString(value.timezone, "policy.timezone"), windowStartLocal: readString(value.windowStartLocal, "policy.windowStartLocal"), windowEndLocal: readString(value.windowEndLocal, "policy.windowEndLocal"), maxDurationMs: readRequiredNumber(value.maxDurationMs, "policy.maxDurationMs"), createdAt: readString(value.createdAt, "policy.createdAt"), updatedAt: readString(value.updatedAt, "policy.updatedAt") };
}
export async function loadExpectedProviderReconnectPolicy(tenantId: string, probeId: string): Promise<ExpectedProviderReconnectPolicy> {
  return readExpectedProviderReconnectPolicy(await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/probes/${encodeURIComponent(probeId)}/expected-provider-reconnect-policy`));
}
export async function saveExpectedProviderReconnectPolicy(tenantId: string, probeId: string, policy: UpdateExpectedProviderReconnectPolicy): Promise<ExpectedProviderReconnectPolicy> {
  return readExpectedProviderReconnectPolicy(await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/probes/${encodeURIComponent(probeId)}/expected-provider-reconnect-policy`, { method: "PUT", body: JSON.stringify(policy) }));
}

export async function saveProbeFirmwarePolicy(
  tenantId: string,
  probeId: string,
  channelOverride: "inherit" | "stable" | "beta",
  updateModeOverride: "inherit" | "manual" | "automatic" | "disabled",
): Promise<void> {
  await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/probes/${encodeURIComponent(probeId)}/firmware-policy`,
    {
      method: "PUT",
      body: JSON.stringify({ channelOverride, updateModeOverride }),
    },
  );
}

export async function requestProbeFirmwareUpdate(tenantId: string, probeId: string): Promise<void> {
  await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/probes/${encodeURIComponent(probeId)}/firmware-update-request`,
    { method: "POST" },
  );
}
